import React, { useState, useRef, useEffect } from 'react';
import './interface.css';
import * as WavEncoder from 'wav-encoder';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMicrophone, faStop, faSpinner } from '@fortawesome/free-solid-svg-icons';

function App() {
    const backend_url = process.env.REACT_APP_BACKEND_URL;

    const [chatHistory, setChatHistory] = useState([]);
    const [isRecording, setIsRecording] = useState(false);
    const [isBotThinking, setIsBotThinking] = useState(false);

    const audioRef = useRef(null);
    const audioContextRef = useRef(null);
    const streamRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const userPromptRef = useRef('');
    const userAudioUrlRef = useRef(null);

    const initializeSession = async () => {
        console.log('initializeSession started');
        let sessionId = localStorage.getItem('sessionId');
        console.log('sessionId after getItem (initial):', sessionId);

        if (!sessionId) {
            console.log('No sessionId found, attempting to create new');
            try {
                const response = await fetch(`${backend_url}/api/new_session`, { method: 'POST' });
                if (response.ok) {
                    const data = await response.json();
                    sessionId = data.session_id;
                    localStorage.setItem('sessionId', sessionId);
                    console.log('New sessionId generated and stored:', sessionId);
                    setChatHistory([]);
                } else {
                    console.error('Failed to generate new session ID');
                    setChatHistory([]);
                }
            } catch (error) {
                console.error('Error generating new session ID:', error);
                setChatHistory([]);
            }
        } else {
            console.log('Existing sessionId found, fetching history...');
            try {
                const response = await fetch(`${backend_url}/api/history/${sessionId}`);
                if (response.ok) {
                    const data = await response.json();
                    console.log('Fetched history for session:', sessionId, data);
                    setChatHistory(data);
                } else {
                    console.error('Failed to fetch initial chat history');
                    setChatHistory([]);
                }
            } catch (error) {
                console.error('Error fetching initial chat history:', error);
                setChatHistory([]);
            }
        }
    };

    useEffect(() => {
        initializeSession();
    }, []);

    useEffect(() => {
        if (chatHistory.length > 0) {
            const latestMessage = chatHistory[chatHistory.length - 1];
            console.log(latestMessage);
            if (latestMessage.sender === 'bot' && latestMessage.audio_url && audioRef.current) {
                const audioPath = `${backend_url}/audio/${latestMessage.audio_url}`;
                audioRef.current.src = audioPath;
                audioRef.current.play().catch(error => {
                    console.error("Error playing audio:", error);
                });
            }
        }
    }, [chatHistory]);

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;

            const mediaRecorder = new MediaRecorder(stream);
            mediaRecorderRef.current = mediaRecorder;

            setIsRecording(true);
            setIsBotThinking(true);

            const chunks = [];

            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    chunks.push(event.data);
                }
            };

            mediaRecorder.onstop = async () => {
                setIsRecording(false);

                if (!audioContextRef.current) {
                    audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
                }

                const audioContext = audioContextRef.current;
                const audioBlob = new Blob(chunks, { type: 'audio/webm;codecs=opus' });
                const audioUrl = URL.createObjectURL(audioBlob);
                userAudioUrlRef.current = audioUrl;

                const fileReader = new FileReader();
                fileReader.onload = async () => {
                    try {
                        const arrayBuffer = fileReader.result;
                        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

                        const wavData = await WavEncoder.encode({
                            sampleRate: audioContext.sampleRate,
                            channelData: [audioBuffer.getChannelData(0)],
                        });

                        const wavBlob = new Blob([wavData], { type: 'audio/wav' });

                        await sendAudioToServer(wavBlob, userPromptRef.current, audioUrl);
                        userPromptRef.current = '';
                        userAudioUrlRef.current = null;
                    } catch (error) {
                        console.error('Error decoding or encoding audio:', error);
                        setChatHistory(prev => [...prev, { sender: 'bot', message: 'Error: Could not process audio for encoding.' }]);
                        setIsBotThinking(false);
                    }
                };

                fileReader.readAsArrayBuffer(audioBlob);
                stream.getTracks().forEach((track) => track.stop());
            };

            mediaRecorder.start();
        } catch (error) {
            console.error('Error accessing microphone:', error);
            setChatHistory(prev => [...prev, { sender: 'bot', message: 'Error: Could not access microphone.' }]);
            setIsBotThinking(false);
        }
    };

    const stopRecording = () => {
        const mediaRecorder = mediaRecorderRef.current;
        if (mediaRecorder && mediaRecorder.state === 'recording') {
            mediaRecorder.stop();
        } else {
            console.warn('MediaRecorder is not recording.');
        }
    };

    const sendAudioToServer = async (wavBlob, userText, userAudioUrl) => {
        setIsBotThinking(true);

        const formData = new FormData();
        formData.append('audio', wavBlob, 'user_audio.wav');

        const voiceSelect = document.getElementById('voiceSelect');
        const selectedVoice = voiceSelect.value;
        formData.append('voice', selectedVoice);

        try {
            const sessionId = localStorage.getItem('sessionId');
            const response = await fetch(`${backend_url}/api/speech`, {
                method: 'POST',
                body: formData,
                headers: {
                    'X-Session-ID': sessionId,
                },
            });

            if (response.ok) {
                const data = await response.json();
                setChatHistory(prev => [
                    ...prev,
                    { sender: 'user', message: data.user_prompt, audio_url: data.user_audio_url },
                    { sender: 'bot', message: data.bot_response, audio_url: data.audio_url },
                ]);
            } else {
                setChatHistory(prev => [...prev, { sender: 'bot', message: 'Error: Unable to get speech response.' }]);
            }
        } catch (error) {
            console.error('Error sending audio to server:', error);
            setChatHistory(prev => [...prev, { sender: 'bot', message: 'Error: Server is unreachable for speech.' }]);
        } finally {
            setIsBotThinking(false);
        }
    };

    const handleNewChat = () => {
        localStorage.removeItem('sessionId');
        initializeSession();
    };

    const handleRecordClick = () => {
        if (!isRecording) {
            startRecording();
        } else {
            stopRecording();
        }
    };

    return (
        <div className="chat-container">
            <div className="chat-box">
                {chatHistory.map((chat, index) => (
                    <div key={index} className={`message ${chat.sender}`}>
                        {chat.sender === 'user' && <div className="user-icon">👤</div>}
                        {chat.sender === 'bot' && <div className="bot-icon">🤖</div>}
                        <p>{chat.message}</p>
                        {chat.audio_url && (
                            <audio ref={audioRef} src={`${backend_url}/audio/${chat.audio_url}`} controls />
                        )}
                    </div>
                ))}
                {isBotThinking && (
                    <div className="message bot thinking">
                        <div className="bot-icon">🤖</div>
                        <FontAwesomeIcon icon={faSpinner} spin />
                        <p>بوٹ جواب دے رہا ہے...</p>
                    </div>
                )}
            </div>
            <div className="input-area">
                <select id="voiceSelect" defaultValue="">
                    <option value="" disabled>Select Voice</option>
                    <option value="ur-PK-UzmaNeural">Uzma</option>
                    <option value="ur-IN-GulNeural">Gul</option>
                    <option value="ur-PK-AsadNeural">Asad</option>
                    <option value="ur-IN-SalmanNeural">Salman</option>
                </select>
                <button onClick={handleRecordClick} className={isRecording ? 'recording' : ''}>
                    <FontAwesomeIcon icon={isRecording ? faStop : faMicrophone} />
                    {isRecording ? ' Stop' : ' Record'}
                </button>
                <button onClick={handleNewChat}>New Chat</button>
            </div>
        </div>
    );
}

export default App;
